import { Menu, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Alert, Button, Col, Container, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import CollapsibleFilters from '../../../components/CollapsibleFilters';
import ManagerBottomNavigationBar from '../../../components/ManagerBottomNavigationBar';
import ManagerGoalCard from '../../../components/ManagerGoalCard';
import SidebarMenu from '../../../components/SidebarMenu';
import SortableList from '../../../components/SortableList';
import UserDropdown from '../../../components/UserDropdown';
import useManagerGoalsStore from '../../../store/goals';

export default function ManagerGoalsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showSidebar, setShowSidebar] = useState(false);
  const {
    filteredGoals,
    selectedUserId,
    isLoading,
    error,
    fetchGoals,
    reorderGoals,
    setSelectedUser,
  } = useManagerGoalsStore();

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  return (
    <CollapsibleFilters.Provider>
      <div className="bg-white min-vh-100 d-flex flex-column">
        <Container fluid className="flex-grow-1 pb-5">
          {/* Sticky Header */}
          <div
            className="sticky-top bg-white"
            style={{
              zIndex: 1020,
              borderBottom: '1px solid rgba(0,0,0,0.1)',
            }}
          >
            {/* Title Bar */}
            <Row className="align-items-center p-4 pb-2">
              <Col xs="auto">
                <Menu
                  size={24}
                  className="text-dark"
                  onClick={() => setShowSidebar(true)}
                  style={{ cursor: 'pointer' }}
                />
              </Col>
              <Col className="d-flex flex-row align-items-center justify-content-center">
                <h2 className="text-dark h5 fw-bold mb-0 me-3">
                  {t('manager.goals.title')}
                </h2>
                <CollapsibleFilters.Button />
              </Col>

              <Col xs="auto">
                <Button
                  variant="primary"
                  size="sm"
                  className="rounded-circle p-2"
                  onClick={() => navigate('/manager/create-goal')}
                >
                  <Plus size={20} />
                </Button>
              </Col>
            </Row>

            {/* Collapsible Filters Content */}
            <CollapsibleFilters.Content>
              <Row className="align-items-center g-3">
                <Col xs={12} sm="auto">
                  <h6 className="mb-0 fw-bold">{t('manager.goals.filters')}</h6>
                </Col>
                <Col xs={12} sm="auto">
                  <UserDropdown
                    selectedUserId={selectedUserId}
                    onUserSelect={setSelectedUser}
                  />
                </Col>
              </Row>
            </CollapsibleFilters.Content>
          </div>

          {/* Content */}
          <div className="px-0 pt-3">
            {isLoading && (
              <div className="text-center py-5">
                <Spinner animation="border" />
              </div>
            )}

            {error && <Alert variant="danger">{error}</Alert>}

            {!isLoading && !error && filteredGoals.length === 0 && (
              <p className="text-center text-muted py-4">
                {selectedUserId
                  ? t('manager.goals.noGoalsForUser')
                  : t('manager.goals.noGoals')}
              </p>
            )}

            {!isLoading &&
              !error &&
              filteredGoals.length > 0 &&
              !selectedUserId && (
                <SortableList
                  items={filteredGoals}
                  getId={g => g.goal._id}
                  onReorder={reorderGoals}
                  renderItem={(goal, _index, dragHandleProps) => (
                    <ManagerGoalCard
                      goal={goal}
                      dragHandleProps={dragHandleProps}
                    />
                  )}
                />
              )}

            {!isLoading &&
              !error &&
              selectedUserId &&
              filteredGoals.map(goal => (
                <ManagerGoalCard key={goal.goal._id} goal={goal} />
              ))}
          </div>
        </Container>

        <SidebarMenu show={showSidebar} onHide={() => setShowSidebar(false)} />
        <ManagerBottomNavigationBar />
      </div>
    </CollapsibleFilters.Provider>
  );
}
